import React from 'react';
import { Route, Routes, Outlet } from 'react-router-dom';

import Dashboard from "../../pages/dashboard/Dashboard";
import Visitors from "../../pages/visitors/Visitors";
import Locations from "../../pages/locations/Locations";
import Users from "../../pages/users/users/Users";
import Roles from "../../pages/users/roles/Roles";
import Kiosks from "../../pages/kiosks/Kiosks";
import RedFlag from "../../pages/redFlag/RedFlags";
import Invitations from "../../pages/configuration/invitations/Invitations";
import Workflow from "../../pages/configuration/workflow/Workflow";
import Agreements from "../../pages/agreements/Agreements";
import VisitorReport from "../../pages/reports/visitorReport/VisitorReport";
import ActivityLog from '../../pages/reports/activityLog/ActivityLog';
import HostReport from '../../pages/reports/hostReport/HostReport';
import RedFlagReport from '../../pages/reports/redFlagReport/RedFlagReport';
import RepeatVisitors from '../../pages/reports/repeatVisitors/RepeatVisitors';
import Account from 'components/pages/administration/account/Account';
import Integration from 'components/pages/administration/integration/Integration';
import VisitorSearch from 'components/pages/search/VisitorSearch';
import NotFound from '../../pages/notFound/NotFound';
import { useSelector } from 'react-redux';
import { RootState } from 'store/store';
import { hasPermission } from 'utils/utils';
import FormFields from 'components/pages/configuration/formFields/FormFields';
import GuestPassReprot from 'components/pages/reports/guestPassReport/GuestPassReport';
import GuestPassLog from 'components/pages/reports/guestPassLog/GuestPassLog';
import Support from 'components/pages/support/Support';
import AvigilonReaders from 'components/pages/configuration/avigilonReaders/AvigilonReaders';
import VisitorsHourlyReport from 'components/pages/reports/visitorsHourlyReport/VisitorsHourlyReport';
import BulkSchedule from 'components/pages/visitors/BulkSchedule';
import NavigateToFirstAvailablePage from './NavigateToFirstAvailablePage';
import EmailTemplates from 'components/pages/configuration/emailTemplates/EmailTemplates';

const Layout: React.FC = () => {
    return (
        <div style={{ height: '100%' }}>
            <Outlet />
        </div>
    );
};

const AppRoutes: React.FC = () => {
    const tokenScopeList = useSelector((state: RootState) => state.auth.auth.data[0]?.tokenScopeList || []);
    const orgId = useSelector((state: RootState) => state.globalOrg.globalOrgId);

    const { isMsiIntegrated, isTokenValid, isGuestPassIntegrated } = useSelector(
        (state: RootState) => state.integration
    );

    const hasLocationRead = hasPermission(tokenScopeList, orgId, 'o', 'location:r');
    const hasUsersRead = hasPermission(tokenScopeList, orgId, 'o', 'user:r');
    const hasRolesRead = hasPermission(tokenScopeList, orgId, 'o', 'role:r');
    const hasKiosksRead = hasPermission(tokenScopeList, orgId, 'o', 'kiosk:r');
    const hasRedFlagConfRead = hasPermission(tokenScopeList, orgId, 'o', 'redflagconf:r');
    const hasInvitationRead = hasPermission(tokenScopeList, orgId, 'o', 'invitation:r');
    const hasSigninconfRead = hasPermission(tokenScopeList, orgId, 'o', 'signinconf:r');
    const hasSignoutconfRead = hasPermission(tokenScopeList, orgId, 'o', 'signoutconf:r');
    const hasFieldsRead = hasPermission(tokenScopeList, orgId, 'o', 'fields:r');
    const hasAgreementsRead = hasPermission(tokenScopeList, orgId, 'o', 'agreements:r');
    const hasEmailTemplatesRead = hasPermission(tokenScopeList, orgId, 'o', 'emailtemplates:r');
    const hasAccountRead = hasPermission(tokenScopeList, orgId, 'o', 'account:r');
    const hasRptVisitorRead = hasPermission(tokenScopeList, orgId, 'o', 'rptvisitor:r');
    const hasRptTrendRead = hasPermission(tokenScopeList, orgId, 'o', 'rpttrend:r');
    const hasRptRedFlagRead = hasPermission(tokenScopeList, orgId, 'o', 'rptredflag:r');
    const hasRptHostRead = hasPermission(tokenScopeList, orgId, 'o', 'rpthost:r');
    const hasRptActivityRead = hasPermission(tokenScopeList, orgId, 'o', 'rptactivity:r');
    const hasRptGuestPassRead = hasPermission(tokenScopeList, orgId, 'o', 'rptguestpass:r');

    const showLocations = hasLocationRead;
    const showUsers = hasUsersRead;
    const showRoles = hasRolesRead;
    const showKiosks = hasKiosksRead;
    const showRedFlag = hasRedFlagConfRead;
    const showInvitations = hasInvitationRead;
    const showWorkflow = hasSigninconfRead || hasSignoutconfRead;
    const showFields = hasFieldsRead;
    const showAgreements = hasAgreementsRead;
    const showEmailTemplates = hasEmailTemplatesRead;
    const showAccount = hasAccountRead;
    const showRptVisitor = hasRptVisitorRead;
    const showRptHourlyVisitors = hasRptVisitorRead;
    const showRptTrend = hasRptTrendRead;
    const showRptRedFlag = hasRptRedFlagRead;
    const showRptHost = hasRptHostRead;
    const showRptActivity = hasRptActivityRead;
    const showRptGuestPass = hasRptGuestPassRead && isGuestPassIntegrated && isTokenValid;
    const showAvigilonReaders = hasSigninconfRead && isMsiIntegrated && isTokenValid;

    return (
        <Routes>
            <Route path="/" element={<NavigateToFirstAvailablePage />} />
            <Route path="/dashboard" element={<Dashboard />} />
            <Route path="/visitors" element={<Visitors />} />
            <Route path="/visitors/bulkSchedule" element={<BulkSchedule />} />
            <Route path="/visitorSearchResults" element={<VisitorSearch />} />
            {showLocations && <Route path="/locations" element={<Locations />} />}
            {showKiosks && <Route path="/kiosks" element={<Kiosks />} />}
            {showRedFlag && <Route path="/redFlag" element={<RedFlag />} />}
            {(showRptVisitor || showRptHourlyVisitors || showRptTrend || showRptRedFlag || showRptHost || showRptActivity || showRptGuestPass) && (
                <Route path="/reports" element={<Layout />}>
                    {showRptVisitor && <Route path="visitorReport" element={<VisitorReport />} />}
                    {showRptHourlyVisitors && <Route path="visitorsHourlyReport" element={<VisitorsHourlyReport />} />}
                    {showRptTrend && <Route path="repeatVisitors" element={<RepeatVisitors />} />}
                    {showRptRedFlag && <Route path="redFlagReport" element={<RedFlagReport />} />}
                    {showRptHost && <Route path="hostReport" element={<HostReport />} />}
                    {showRptActivity && <Route path="activityLog" element={<ActivityLog />} />}
                    {showRptGuestPass && <Route path="guestPassReport" element={<GuestPassReprot />} />}
                    {showRptGuestPass && <Route path="guestPassLog" element={<GuestPassLog />} />}
                </Route>
            )}
            {(showInvitations || showWorkflow || showFields || showAgreements || showUsers || showRoles || showEmailTemplates || showAvigilonReaders) && (
                <Route path="/configuration" element={<Layout />}>
                    {showInvitations && <Route path="invitations" element={<Invitations />} />}
                    {showWorkflow && <Route path="workflow" element={<Workflow />} />}
                    {showFields && <Route path="formFields" element={<FormFields />} />}
                    {showAgreements && <Route path="agreements" element={<Agreements />} />}
                    {showUsers && <Route path="users" element={<Users />} />}
                    {showRoles && <Route path="userRoles" element={<Roles />} />}
                    {showEmailTemplates && <Route path="emailTemplates" element={<EmailTemplates />} />}
                    {showAvigilonReaders && <Route path="avigilonReaders" element={<AvigilonReaders />} />}
                </Route>
            )}
            <Route path="/administration" element={<Layout />}>
                {showAccount && <Route path="account" element={<Account />} />}
                <Route path="integration" element={<Integration />} />
            </Route>
            F            <Route path="/support" element={<Support />} />
            <Route path="*" element={<NotFound />} />
        </Routes>
    );
};

export default AppRoutes;
